import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Sparkles as SparklesIcon, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

const STAGES = [
  { value: 'raw', label: 'Raw', hint: 'Just captured' },
  { value: 'thinking', label: 'Thinking', hint: 'Exploring it' },
  { value: 'promoted', label: 'Promoted', hint: 'Committed or shipped to Goals' },
];

const FILTER_TABS = [
  { value: 'raw', label: 'Raw' },
  { value: 'thinking', label: 'Thinking' },
  { value: 'promoted', label: 'Promoted' },
  { value: 'all', label: 'All' },
];

/** Legacy rows used status "backlog"; treat like raw. */
function normalizeIdeaStatus(idea) {
  const s = idea?.status;
  if (!s || s === 'backlog') return 'raw';
  if (s === 'thinking' || s === 'promoted') return s;
  return 'raw';
}

function categoryKey(idea) {
  return (idea.category || idea.idea_type || 'wildcard').toLowerCase();
}

const categoryBadgeClass = {
  product: 'bg-lavender-light text-lavender',
  marketing: 'bg-amber-light text-amber',
  content: 'bg-teal-light text-teal',
  partnership: 'bg-sage-light text-sage',
  pivot: 'bg-error-light text-error',
  wildcard: 'bg-gold-light text-gold',
};

const statusBadgeClass = {
  raw: 'bg-bg-surface-alt text-text-secondary border border-border',
  thinking: 'bg-amber-light text-amber',
  promoted: 'bg-gold-light text-gold',
};

const IdeasPage = () => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('raw');
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchIdeas = useCallback(async () => {
    try {
      const data = await apiFetch('/api/ideas');
      setIdeas(data.ideas || []);
    } catch (error) {
      console.error('Error fetching ideas:', error);
      toast.error(error?.message || 'Could not load ideas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  const promoteIdea = async (ideaId) => {
    try {
      await apiFetch(`/api/ideas/${ideaId}/promote`, { method: 'POST' });
      toast.success('Idea promoted — new goal created in Goals');
      fetchIdeas();
    } catch (error) {
      console.error('Error promoting idea:', error);
      toast.error(error?.message || 'Failed to promote idea');
    }
  };

  const patchIdeaStatus = async (idea, nextStatus) => {
    const current = normalizeIdeaStatus(idea);
    if (nextStatus === current) return;
    try {
      await apiFetch(`/api/ideas/${idea.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus }),
      });
      toast.success('Stage updated');
      fetchIdeas();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Could not update stage');
    }
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/api/ideas/${deleteTarget.id}`, { method: 'DELETE' });
    toast.success('Idea removed');
    fetchIdeas();
  };

  const filteredIdeas = ideas.filter((idea) => {
    if (filter === 'all') return true;
    return normalizeIdeaStatus(idea) === filter;
  });

  const emptyCopy =
    filter === 'all'
      ? { title: 'No ideas yet', body: 'Start capturing your sparks of inspiration' }
      : {
          title: `No ${filter} ideas`,
          body: 'Switch tabs or add a new idea',
        };

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="ideas-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Idea Vault</h1>
            <p className="text-text-secondary">Raw capture → thinking → promote to a goal when ready</p>
          </div>
          <button
            type="button"
            onClick={() => setFormModal({ mode: 'create' })}
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-idea-button"
          >
            <Plus size={20} />
            New Idea
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {FILTER_TABS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                filter === value
                  ? 'bg-gold text-white border-gold'
                  : 'bg-bg-surface text-text-secondary border-border hover:border-gold'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
          </div>
        ) : filteredIdeas.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-lavender-light rounded-full flex items-center justify-center mx-auto mb-4">
              <SparklesIcon className="text-lavender" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">{emptyCopy.title}</h3>
            <p className="text-text-secondary mb-6">{emptyCopy.body}</p>
            {filter === 'all' || filter === 'raw' ? (
              <button
                type="button"
                onClick={() => setFormModal({ mode: 'create' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
              >
                <Plus size={20} />
                Add First Idea
              </button>
            ) : null}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredIdeas.map((idea) => {
              const ck = categoryKey(idea);
              const badgeCls = categoryBadgeClass[ck] || categoryBadgeClass.wildcard;
              const stage = normalizeIdeaStatus(idea);
              const stageCls = statusBadgeClass[stage] || statusBadgeClass.raw;
              const canPromote = !idea.promoted_goal_id;

              return (
                <div
                  key={idea.id}
                  className="bg-bg-surface rounded-2xl p-6 border border-border card-hover flex flex-col"
                >
                  <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${badgeCls}`}>
                      {ck}
                    </span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${stageCls}`}>
                      {stage}
                    </span>
                  </div>

                  <p className="text-text-primary leading-relaxed mb-4 flex-1 whitespace-pre-wrap">{idea.idea_text}</p>

                  <div className="space-y-3 pt-2 border-t border-border">
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Stage</label>
                      <select
                        value={stage}
                        onChange={(e) => patchIdeaStatus(idea, e.target.value)}
                        className="w-full px-3 py-2 bg-bg-surface border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                        aria-label="Idea stage"
                      >
                        {STAGES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label} — {s.hint}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {canPromote ? (
                        <button
                          type="button"
                          onClick={() => promoteIdea(idea.id)}
                          className="flex-1 min-w-[140px] py-2 bg-sage hover:opacity-90 text-white font-medium rounded-lg transition-all text-sm"
                        >
                          Promote to goal
                        </button>
                      ) : (
                        <div className="flex-1 py-2 px-3 bg-sage-light text-sage text-sm font-medium rounded-lg text-center">
                          Linked to goal ✓
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setFormModal({ mode: 'edit', idea })}
                        className="p-2 rounded-lg border border-border text-text-secondary hover:bg-gold-light hover:text-gold transition-colors"
                        aria-label="Edit idea"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            id: idea.id,
                            preview: idea.idea_text?.slice(0, 100),
                          })
                        }
                        className="p-2 rounded-lg border border-border text-text-secondary hover:bg-error-light hover:text-error transition-colors"
                        aria-label="Delete idea"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {formModal ? (
          <IdeaFormModal
            key={formModal.mode === 'edit' ? formModal.idea.id : 'new'}
            mode={formModal.mode}
            idea={formModal.mode === 'edit' ? formModal.idea : null}
            onClose={() => setFormModal(null)}
            onSave={fetchIdeas}
          />
        ) : null}

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Delete this idea?"
          message="This will permanently remove it from your vault."
          detail={deleteTarget?.preview}
          confirmLabel="Delete"
          onConfirm={performDelete}
        />
      </div>
    </div>
  );
};

function IdeaFormModal({ mode, idea, onClose, onSave }) {
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState({
    idea_text: '',
    idea_type: 'wildcard',
    status: 'raw',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idea) {
      setFormData({
        idea_text: idea.idea_text || '',
        idea_type: idea.category || idea.idea_type || 'wildcard',
        status: normalizeIdeaStatus(idea),
      });
    }
  }, [idea]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await apiFetch(`/api/ideas/${idea.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            idea_text: formData.idea_text,
            idea_type: formData.idea_type,
            status: formData.status,
          }),
        });
        toast.success('Idea updated');
      } else {
        await apiFetch('/api/ideas', {
          method: 'POST',
          body: JSON.stringify({
            idea_text: formData.idea_text,
            idea_type: formData.idea_type,
            status: formData.status,
          }),
        });
        toast.success('Idea captured');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-overlay-in" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal-in custom-scrollbar" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-3xl font-bold text-text-primary mb-6">
          {isEdit ? 'Edit idea' : 'Capture an idea'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What&apos;s the idea?</label>
            <textarea
              value={formData.idea_text}
              onChange={(e) => setFormData({ ...formData, idea_text: e.target.value })}
              placeholder="Brain dump here…"
              rows={4}
              required
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
              data-testid="idea-text-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Type</label>
            <select
              value={formData.idea_type}
              onChange={(e) => setFormData({ ...formData, idea_type: e.target.value })}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="product">Product</option>
              <option value="marketing">Marketing</option>
              <option value="content">Content</option>
              <option value="partnership">Partnership</option>
              <option value="pivot">Pivot</option>
              <option value="wildcard">Wildcard</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Stage</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
            >
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label} — {s.hint}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-tertiary mt-2">
              Use <strong>Promoted</strong> when you&apos;re committed; use <strong>Promote to goal</strong> on the card to
              create a linked goal.
            </p>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.idea_text.trim()}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save idea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IdeasPage;
